import { Link, Outlet, createFileRoute, useLocation } from "@tanstack/react-router";
import { ClipboardList, QrCode, Hexagon, Clock, FilePlus2 } from "lucide-react";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/review")({
  component: ReviewLayout,
  head: () => ({
    meta: [
      { title: "Review — MissionReady" },
      {
        name: "description",
        content:
          "Mission Ready review hub: site reports, guard tours, geofence breaches, and timecards.",
      },
    ],
  }),
});

const TABS = [
  {
    to: "/review/reports" as const,
    label: "Site Reports",
    sub: "Reports submitted by guards",
    icon: ClipboardList,
  },
  {
    to: "/review/tours" as const,
    label: "Guard Tours",
    sub: "Patrols, scans, completion",
    icon: QrCode,
  },
  {
    to: "/review/geofence" as const,
    label: "Geofence Breaches",
    sub: "Perimeter & exit events",
    icon: Hexagon,
  },
  {
    to: "/review/timecards" as const,
    label: "Timecards",
    sub: "Punches & corrections",
    icon: Clock,
  },
  {
    to: "/review/punch-requests" as const,
    label: "Punch Requests",
    sub: "Guard missed punches & corrections",
    icon: FilePlus2,
  },
];

function ReviewLayout() {
  const location = useLocation();
  const path = location.pathname;
  return (
    <div className="min-h-dvh bg-[#0D0D0D] text-white">
      <header className="border-b border-white/10 px-4 pt-5 pb-3 md:px-8">
        <div className="text-[11px] uppercase tracking-[0.18em] text-[#C0C0C0]">Mission Ready</div>
        <h1 className="mt-1 text-2xl font-semibold md:text-3xl">Review</h1>
        <p className="mt-1 text-sm text-[#999999]">
          Audit guard activity, reports, and timecards across your sites.
        </p>
      </header>

      <div className="px-4 py-5 md:px-8">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          {TABS.map((t) => {
            const active = path === t.to || path.startsWith(t.to + "/");
            const Icon = t.icon;
            return (
              <Link
                key={t.to}
                to={t.to}
                className={cn(
                  "group rounded-xl border p-4 transition-all",
                  active
                    ? "border-[#CC0000] bg-[#CC0000]/10 shadow-[0_0_0_1px_rgba(204,0,0,0.4)]"
                    : "border-white/10 bg-white/[0.02] hover:border-[#CC0000]/40 hover:bg-white/[0.05]",
                )}
              >
                <div className="flex items-center gap-2">
                  <div
                    className={cn(
                      "flex h-9 w-9 items-center justify-center rounded-lg",
                      active ? "bg-[#CC0000] text-white" : "bg-white/10 text-[#C0C0C0]",
                    )}
                  >
                    <Icon className="h-5 w-5" />
                  </div>
                  <div
                    className={cn(
                      "text-sm font-semibold",
                      active ? "text-white" : "text-white/90",
                    )}
                  >
                    {t.label}
                  </div>
                </div>
                <div className="mt-2 text-[12px] text-[#999999]">{t.sub}</div>
              </Link>
            );
          })}
        </div>

        <div className="mt-6">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
